import React, { useState, useEffect } from "react";
import axios from "../../api/axiosConfig";
import { toast } from "react-toastify";
import {
  ArrowLeft,
  Loader,
  Users,
  User,
  Star,
  Mail,
  Phone,
  Bike,
  Check,
  X,
  Trash2,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function CompanyRyders() {
  const [ryders, setRyders] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [filter, setFilter] = useState("all");

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  // ✅ Extraer el ID de la empresa (UUID, no el objeto completo)
  const companyId = user.company?.id ?? user.company?._id ?? user.company;

  useEffect(() => {
    fetchRyders();
    // eslint-disable-next-line
  }, [filter]);

  const fetchRyders = async () => {
    try {
      const params = {};

      // ✅ UUID check (no regex de MongoDB ObjectId)
      if (companyId) {
        params.companyId = companyId;
      }

      if (filter !== "all") {
        params.approvalStatus = filter;
      }

      // ✅ Endpoint correcto: /ryders?companyId=... (no /ryders/company)
      const res = await axios.get("/ryders", { params });
      // ✅ Extraer del formato { success, data: { data: [...], total } }
      setRyders(res.data?.data?.data ?? res.data?.data ?? res.data ?? []);
      setLoading(false);
    } catch (error) {
      console.error("Error al cargar domiciliarios:", error);
      toast.error("Error al cargar domiciliarios");
      setLoading(false);
    }
  };

  const handleApprove = async (ryderId) => {
    try {
      // ✅ Endpoint correcto: PATCH /ryders/:id/approval con body { approvalStatus }
      await axios.patch(`/ryders/${ryderId}/approval`, { approvalStatus: "aprobado" });
      toast.success("Domiciliario aprobado exitosamente");
      fetchRyders();
    } catch (error) {
      toast.error("Error al aprobar domiciliario");
    }
  };

  const handleReject = async (ryderId) => {
    if (!window.confirm("¿Estás seguro de rechazar este domiciliario?")) return;

    try {
      // ✅ Endpoint correcto: PATCH /ryders/:id/approval con body { approvalStatus }
      await axios.patch(`/ryders/${ryderId}/approval`, { approvalStatus: "rechazado" });
      toast.success("Domiciliario rechazado");
      fetchRyders();
    } catch (error) {
      toast.error("Error al rechazar domiciliario");
    }
  };

  const handleDelete = async (ryderId) => {
    if (!window.confirm("¿Estás seguro de eliminar este domiciliario?")) return;

    try {
      await axios.delete(`/ryders/${ryderId}`);
      toast.success("Domiciliario eliminado");
      fetchRyders();
    } catch (error) {
      const msg = error.response?.data?.message || "Error al eliminar";
      toast.error(msg);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      disponible: {
        bg: "bg-green-100",
        text: "text-green-800",
        label: "Disponible",
      },
      ocupado: {
        bg: "bg-yellow-100",
        text: "text-yellow-800",
        label: "Ocupado",
      },
      offline: {
        bg: "bg-gray-100",
        text: "text-gray-800",
        label: "Desconectado",
      },
    };
    const badge = badges[status] || badges.offline;
    return (
      <span
        className={`px-3 py-1 rounded-full text-xs font-semibold ${badge.bg} ${badge.text}`}
      >
        {badge.label}
      </span>
    );
  };

  const getApprovalBadge = (status) => {
    const badges = {
      pendiente: {
        bg: "bg-yellow-100",
        text: "text-yellow-800",
        label: "Pendiente",
      },
      aprobado: {
        bg: "bg-green-100",
        text: "text-green-800",
        label: "Aprobado",
      },
      rechazado: { bg: "bg-red-100", text: "text-red-800", label: "Rechazado" },
    };
    const badge = badges[status] || badges.pendiente;
    return (
      <span
        className={`px-3 py-1 rounded-full text-xs font-semibold ${badge.bg} ${badge.text}`}
      >
        {badge.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader className="animate-spin text-supernova-500" size={48} />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 mb-4 text-supernova-600 hover:text-supernova-700"
      >
        <ArrowLeft size={20} />
        Volver
      </button>
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1
            className="text-3xl font-bold text-gray-800 mb-2"
            data-testid="company-ryders-title"
          >
            Gestión de Domiciliarios
          </h1>
          <p className="text-gray-600">
            Administra los domiciliarios de tu empresa
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
          <div className="flex gap-2 overflow-x-auto">
            <button
              onClick={() => setFilter("all")}
              className={`px-4 py-2 rounded-lg font-semibold transition whitespace-nowrap ${
                filter === "all"
                  ? "bg-supernova-500 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
              data-testid="filter-all"
            >
              Todos ({ryders.length})
            </button>
            <button
              onClick={() => setFilter("pendiente")}
              className={`px-4 py-2 rounded-lg font-semibold transition whitespace-nowrap ${
                filter === "pendiente"
                  ? "bg-supernova-500 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
              data-testid="filter-pendiente"
            >
              Pendientes
            </button>
            <button
              onClick={() => setFilter("aprobado")}
              className={`px-4 py-2 rounded-lg font-semibold transition whitespace-nowrap ${
                filter === "aprobado"
                  ? "bg-supernova-500 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
              data-testid="filter-aprobado"
            >
              Aprobados
            </button>
          </div>
        </div>

        {ryders.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm p-12 text-center">
            <Users size={64} className="mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500 text-lg">
              No hay domiciliarios para mostrar
            </p>
            <p className="text-gray-400 text-sm mt-2">
              Los domiciliarios que se registren con tu empresa aparecerán aquí
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {ryders.map((ryder) => (
              <div
                key={ryder.id}
                className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition"
                data-testid="ryder-card"
              >
                <div className="p-6">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-16 h-16 bg-supernova-100 rounded-full flex items-center justify-center">
                      {ryder.avatar ? (
                        <img
                          src={ryder.avatar}
                          alt={ryder.user?.name}
                          className="w-full h-full object-cover rounded-full"
                        />
                      ) : (
                        <User className="text-supernova-600" size={32} />
                      )}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-lg text-gray-800">
                        {ryder.user?.name}
                      </h3>
                      <div className="flex items-center gap-1 mt-1">
                        <Star className="text-yellow-500" size={16} />
                        <span className="text-sm font-semibold">
                          {ryder.totalDeliveries > 0
                            ? Number(ryder.rating).toFixed(1)
                            : "Nuevo"}
                        </span>
                        <span className="text-xs text-gray-500">
                          ({ryder.totalDeliveries ?? 0})
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-2 text-sm">
                      <Mail size={16} className="text-gray-400" />
                      <span className="text-gray-600">{ryder.user?.email}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Phone size={16} className="text-gray-400" />
                      <span className="text-gray-600">{ryder.user?.phone}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Bike size={16} className="text-gray-400" />
                      <span className="text-gray-600 capitalize">
                        {/* ✅ vehicleType/vehiclePlate son campos directos */}
                        {ryder.vehicleType}{" "}
                        {ryder.vehiclePlate && `- ${ryder.vehiclePlate}`}
                      </span>
                    </div>
                  </div>

                  <div className="flex gap-2 mb-4">
                    {getStatusBadge(ryder.status)}
                    {getApprovalBadge(ryder.approvalStatus)}
                  </div>

                  {ryder.approvalStatus === "pendiente" && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleApprove(ryder.id)}
                        className="flex-1 bg-green-500 text-white py-2 rounded-lg font-semibold hover:bg-green-600 flex items-center justify-center gap-1"
                        data-testid="approve-ryder-button"
                      >
                        <Check size={18} />
                        Aprobar
                      </button>
                      <button
                        onClick={() => handleReject(ryder.id)}
                        className="flex-1 bg-red-500 text-white py-2 rounded-lg font-semibold hover:bg-red-600 flex items-center justify-center gap-1"
                        data-testid="reject-ryder-button"
                      >
                        <X size={18} />
                        Rechazar
                      </button>
                    </div>
                  )}

                  {ryder.approvalStatus === "aprobado" && (
                    <button
                      onClick={() => handleDelete(ryder.id)}
                      className="w-full bg-red-100 text-red-600 py-2 rounded-lg font-semibold hover:bg-red-200 flex items-center justify-center gap-1"
                      data-testid="delete-ryder-button"
                    >
                      <Trash2 size={18} />
                      Eliminar
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
